import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import withAuth from "../../../hoc/withAuth/withAuth";
import AnimatedCard from "../../../components/AnimatedCard/AnimatedCard";
import { Button, colors, InnerCard } from "@repo/ui";
import PageContainer from "../../../components/PageContainer/PageContainer";
import { RootState } from "../../../redux/store";
import { useAppDispatch, useAppSelector } from "../../../hooks/useAppDispatch";
import {
  confirmPayment,
  createPayment,
} from "../../../services/stripeApiService";
import { publishGiftTable } from "../../../redux/slices/giftTableSlice";
import { validateTransactionToken } from "../../../redux/slices/pricingSlice";
import { withTranslations } from "../../../hoc/withTranslations/withTranslations";

const LOCALE = "modules_MyGiftTable_pages_PrepayPage";

const PrepayPage: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const giftTableID = useAppSelector((state: RootState) => state.giftTable.ID);
  const userID = useAppSelector((state: RootState) => state.auth.user?.id);
  const userName = useAppSelector((state: RootState) => state.auth.user?.name);
  const totalCharge = useAppSelector(
    (state: RootState) => state.pricing.purchaseState.totalCharge
  );
  const pricingToken = useAppSelector(
    (state: RootState) => state.pricing.purchaseState.token
  );
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    setLoading(true);
    try {
      // Create PaymentMethod
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: { name: userName },
      });
      if (error) {
        setErrorMessage(error.message || t("payment-error"));
        setLoading(false);
        return;
      }

      if (paymentMethod && giftTableID && userID && totalCharge) {
        // Create PaymentIntent on the server and receive clientSecret
        const paymentResponse = await createPayment({
          user_id: userID,
          metadata: { gift_table_id: giftTableID, pricing_token: pricingToken },
          amount: Math.floor(totalCharge * 100),
          currency: "MXN",
          payment_type: "host_prepaid",
        });

        // Confirm payment on the client using confirmCardPayment
        const { error: confirmError, paymentIntent } =
          await stripe.confirmCardPayment(paymentResponse.clientSecret, {
            payment_method: paymentMethod!.id,
          });

        if (confirmError) {
          setErrorMessage(confirmError.message || t("payment-failed"));
          setLoading(false);
          return;
        }
        if (paymentIntent.status !== "succeeded") {
          setErrorMessage(t("payment-failed"));
          setLoading(false);
          return;
        }

        // Update payment status in backend (Firestore update)
        const finalConfirmation = await confirmPayment(
          paymentResponse.paymentIntentID
        );
        if (finalConfirmation.status === "succeeded") {
          await dispatch(
            validateTransactionToken({
              token: pricingToken,
              paymentId: finalConfirmation.paymentId,
              transactionType: "purchase",
            })
          ).unwrap();
          await dispatch(publishGiftTable(giftTableID));
          navigate("/my-table/payment-success");
        } else {
          setErrorMessage(t("payment-failed"));
        }
      }
    } catch (error) {
      setErrorMessage(t("payment-failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer startColor="white" endColor={colors.background}>
      <div className="mt-16"></div>
      <AnimatedCard title={t("title")} className="bg-white max-w-4xl w-full">
        <form onSubmit={handlePayment}>
          <p className="mb-8">{t("description")}</p>
          <div className="flex flex-col items-center mb-4">
            <InnerCard className="flex flex-col p-8 items-center text-center">
              <h3 className="font-bold text-xl mb-4">{t("one-time-title")}</h3>
              <p className="text-3xl mb-4">
                {totalCharge
                  ? totalCharge.toLocaleString("es-MX", {
                      style: "currency",
                      currency: "MXN",
                    })
                  : t("calculating")}
              </p>
              <p className="text-sm">{t("one-time-description")}</p>
            </InnerCard>
            <div className="w-full mt-8">
              <CardElement className="p-4 border rounded-md" />
            </div>
            {errorMessage && (
              <p className="text-red-500 mt-4">{errorMessage}</p>
            )}
            <Button
              label={loading ? "Processing..." : t("pay-now-button")}
              onClick={handlePayment}
              type="primary"
              className="w-full mt-4"
              disabled={loading}
            />
          </div>
        </form>
      </AnimatedCard>
    </PageContainer>
  );
};

export default withAuth(withTranslations(LOCALE)(PrepayPage));
